import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { PERSONALIZATION_DEFAULT_CONFIG } from '../../../shared/constants';
import { DEFAULT_LIST_COLUMNS } from '../../../shared/constants/entitlements';
import { useLocalize, usePersonalization } from '../../../shared/hooks';
import { useLazyGetColumnPickerQuery } from '../../ViolationsTab/data-layer/violationsTabApi';
import { type ColumnPickerState } from '../../ViolationsTab/data-layer/violationsTabTypes';
import { entitlementActions } from '../data-store/entitlementSlice';

/**
 * Custom hook to initialize and manage column picker configuration for the Violations tab view.
 *
 * This hook is responsible for fetching column definitions from an API or falling back to a default configuration
 * if the API fails or is unstable. The resulting list of columns is processed to apply localization, determine default selections,
 * and then dispatched to the Redux store to be used in the UI.
 *
 * @returns {{
 *   initializeViewColumns: () => Promise<boolean>
 * }} An object containing a function `initializeViewColumns` that initializes the column picker state and returns a promise that resolves to `true` upon success.
 *
 * @example
 * const { initializeViewColumns } = useColumnPicker();
 * useEffect(() => {
 *   initializeViewColumns();
 * }, []);
 */
const useEntitlementColumnPicker = () => {
  const l = useLocalize();
  const dispatch = useDispatch();

  const { getPersonlization } = usePersonalization();

  const savedColumns = useMemo(() => {
    const personalization = getPersonlization();

    return PERSONALIZATION_DEFAULT_CONFIG.saveColumn &&
      personalization &&
      personalization.columns
      ? personalization.columns
      : null;
  }, [getPersonlization]);

  const defaultColumns = useMemo(() => DEFAULT_LIST_COLUMNS, []);

  // Map API column IDs to actual data field names
  const columnIdToFieldMapping = useMemo(
    () =>
      ({
        riskPriority: 'riskPriority',
        riskType: 'riskType',
        riskName: 'riskName',
        entitlementName: 'entitlementDisplayName',
        entitlementType: 'entitlementTypeName',
      }) as Record<string, string>,
    []
  );

  const [fetchColumnPicker] = useLazyGetColumnPickerQuery();

  const initializeViewColumns = useCallback(async () => {
    try {
      const response = (await fetchColumnPicker(
        'inherent_entitlement'
      ).unwrap()) as Record<string, ColumnPickerState[]>;

      const columnsList: ColumnPickerState[] = Object.values(response).flat();
      const modifiedColumnsList = columnsList
        .map((column) => ({
          label: l(column.localizationKey),
          value: columnIdToFieldMapping[column.id] || column.id,
          isChecked:
            savedColumns !== null
              ? savedColumns.includes(column.id)
              : column.isDefault,
          isDefault: defaultColumns.includes(column.id),
          isDisabled: false,
        }))
        .sort((b, a) => Number(a.isChecked) - Number(b.isChecked));

      dispatch(entitlementActions.setViewColumns(modifiedColumnsList));

      return true;
    } catch {
      Logger.log('Error fetching entitlement column picker');

      return false;
    }
  }, [
    l,
    defaultColumns,
    savedColumns,
    dispatch,
    fetchColumnPicker,
    columnIdToFieldMapping,
  ]);

  return { initializeViewColumns };
};

export default useEntitlementColumnPicker;
